import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Plus, Trash2, Save, Instagram, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, getAdminToken } from "../../lib/api";

/**
 * Admin · Instagram Posts manager.
 *
 * Paste post permalinks (`https://www.instagram.com/p/SHORTCODE/`) one per
 * row. The homepage "Follow" section embeds them via Instagram's official
 * oEmbed widget — no API key, no token, no scraping.
 *
 * To get a permalink:
 *   1. Open the post on instagram.com/egemodels
 *   2. Hit ⋯ → Copy link  (or grab the URL from the address bar)
 *   3. Paste here, hit Save. Goes live instantly.
 */
const PERMALINK_HINT =
  "https://www.instagram.com/p/SHORTCODE/   or   https://www.instagram.com/reel/SHORTCODE/";

const IG_RX = /^https?:\/\/(www\.)?instagram\.com\/(p|reel|tv)\/[A-Za-z0-9_-]+\/?(\?.*)?$/i;

export default function InstagramManager() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([""]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin/login");
      return;
    }
    api.get("/site/settings")
      .then((r) => {
        const posts = Array.isArray(r.data?.instagram_posts) ? r.data.instagram_posts : [];
        setRows(posts.length ? posts : [""]);
      })
      .catch(() => setRows([""]))
      .finally(() => setLoading(false));
  }, [navigate]);

  const update = (i, val) => {
    const next = [...rows];
    next[i] = val;
    setRows(next);
  };

  const add = () => setRows([...rows, ""]);
  const remove = (i) => setRows(rows.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = rows.map((r) => (r || "").trim()).filter(Boolean);
      const res = await api.put("/admin/site/instagram-posts", { urls: cleaned });
      const saved = res.data?.instagram_posts || [];
      setRows(saved.length ? saved : [""]);
      toast.success(`Saved · ${saved.length} post${saved.length === 1 ? "" : "s"} live`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-[#0A0A0A] pt-32 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div data-testid="instagram-manager-page" className="min-h-screen bg-white text-[#0A0A0A] pt-24">
      <div className="max-w-3xl mx-auto px-6 lg:px-12 pb-24">
        <Link
          to="/admin"
          data-testid="ig-manager-back"
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-black"
        >
          <ChevronLeft size={14} /> Back to dashboard
        </Link>

        <div className="mt-6">
          <p className="label-overline text-zinc-500">Site settings · 02</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light mt-3 leading-none">
            Instagram posts.
          </h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-2xl leading-relaxed">
            Paste the permalinks of the posts you want featured on the homepage "Follow"
            section. Up to 24 posts, in display order. Goes live instantly — no redeploy.
          </p>

          <div className="mt-6 bg-zinc-50 border border-black/10 px-4 py-3 text-xs text-zinc-600 leading-relaxed">
            <p className="font-medium text-zinc-800 mb-1">How to get a post URL</p>
            <ol className="list-decimal pl-5 space-y-0.5">
              <li>
                Open a post on{" "}
                <a
                  href="https://instagram.com/egemodels"
                  target="_blank"
                  rel="noreferrer"
                  className="link-underline inline-flex items-center gap-1"
                >
                  instagram.com/egemodels <ExternalLink size={10} />
                </a>
              </li>
              <li>Tap the ⋯ menu → <span className="font-medium">Copy link</span> (or grab the URL from the address bar)</li>
              <li>Paste it below. Format: <span className="font-mono text-[11px]">{PERMALINK_HINT}</span></li>
            </ol>
          </div>
        </div>

        <div className="mt-10 space-y-3">
          {rows.map((url, i) => {
            const trimmed = (url || "").trim();
            const isValid = !trimmed || IG_RX.test(trimmed);
            return (
              <div key={i} className="flex items-stretch gap-2">
                <span className="flex items-center justify-center w-9 text-xs font-mono text-zinc-400 border border-black/10 bg-white">
                  {i + 1}
                </span>
                <input
                  type="url"
                  inputMode="url"
                  value={url}
                  onChange={(e) => update(i, e.target.value)}
                  placeholder="https://www.instagram.com/p/SHORTCODE/"
                  data-testid={`ig-row-${i}`}
                  className={`flex-1 px-3 py-2.5 text-sm bg-white border ${
                    isValid ? "border-black/15 focus:border-black" : "border-red-500"
                  } outline-none transition-colors font-mono`}
                />
                <button
                  type="button"
                  onClick={() => remove(i)}
                  data-testid={`ig-remove-${i}`}
                  disabled={rows.length === 1}
                  className="flex items-center justify-center w-10 border border-black/10 bg-white text-zinc-500 hover:text-black hover:border-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Remove row"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}

          <button
            type="button"
            onClick={add}
            disabled={rows.length >= 24}
            data-testid="ig-add-row"
            className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-zinc-700 hover:text-black border border-dashed border-black/20 hover:border-black w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Plus size={12} /> Add another post {rows.length >= 24 ? "(max 24)" : ""}
          </button>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-black/10 pt-6">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="ig-save"
            className="btn-primary inline-flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save size={14} /> Save & publish
              </>
            )}
          </button>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="btn-ghost inline-flex items-center gap-2"
          >
            <Instagram size={14} /> Preview homepage
          </a>
        </div>

        <p className="text-xs text-zinc-500 mt-8 leading-relaxed">
          Posts render through Instagram's official embed widget. Only public posts from
          public accounts can be embedded — if you've set the account to private, the
          tiles will show "View on Instagram" links instead of the post preview.
        </p>
      </div>
    </div>
  );
}
